import { useEffect, useRef } from "react";
import { attachAutoplay } from "@/lib/autoplay-video";

type Props = {
  src: string;
  className?: string;
  loop?: boolean;
  onEnded?: () => void;
  onError?: () => void;
  ariaHidden?: boolean;
};

/**
 * Vidéo décorative garantie muette et en lecture automatique sur iOS.
 *
 * Point clé : Safari iOS décide d'autoriser (ou non) la lecture automatique au
 * moment où la source est chargée. Les attributs `muted` / `playsinline`
 * doivent donc exister sur l'élément AVANT que la source ne soit posée — ce que
 * React ne garantit pas (il applique `muted` comme propriété, souvent après le
 * `src`). On rend donc la balise sans `src` et on l'assigne nous-mêmes.
 */
export function AutoVideo({ src, className, loop = true, onEnded, onError, ariaHidden }: Props) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;

    // 1. Attributs de compatibilité posés en premier.
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.setAttribute("autoplay", "");
    video.removeAttribute("controls");
    video.controls = false;
    video.playsInline = true;
    video.autoplay = true;
    video.loop = loop;
    video.disableRemotePlayback = true;
    video.preload = "auto"; // effectif une fois la source posée

    // 2. Source posée seulement quand la vidéo approche de l'écran, puis lecture fiabilisée.
    let detach: (() => void) | void;
    const start = () => {
      if (video.getAttribute("src") !== src) {
        video.setAttribute("src", src);
        video.load();
      }
      detach = attachAutoplay(video);
    };
    if (typeof IntersectionObserver === "undefined") {
      start();
      return () => detach?.();
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          start();
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(video);
    return () => {
      io.disconnect();
      detach?.();
    };
  }, [src, loop]);

  return (
    <video
      ref={ref}
      autoPlay
      muted
      loop={loop}
      playsInline
      preload="none"
      controls={false}
      disablePictureInPicture
      onEnded={onEnded}
      onError={onError}
      aria-hidden={ariaHidden}
      className={className}
    >
      <track
        kind="captions"
        srcLang="fr"
        label="Sans dialogue"
        default
        src="data:text/vtt,WEBVTT"
      />
    </video>
  );
}
